import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import { getCoverUrl } from '../utils/newsImage';
import '../noticias-page.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const CATEGORY_COLORS = {
  PARCHE:  { bg: 'rgba(0,120,255,0.20)',  border: 'rgba(0,120,255,0.50)',  text: '#69CCF0' },
  EVENTO:  { bg: 'rgba(255,140,0,0.16)',  border: 'rgba(255,140,0,0.50)',  text: '#FFAA44' },
  PVP:     { bg: 'rgba(200,30,30,0.18)',  border: 'rgba(220,40,40,0.55)',  text: '#FF6E6E' },
  AVISO:   { bg: 'rgba(160,50,255,0.16)', border: 'rgba(160,50,255,0.50)', text: '#B080FF' },
};

const ACTIVE_CATEGORIES = ['PARCHE', 'EVENTO', 'PVP', 'AVISO'];

function CategoryBadge({ category }) {
  const cfg = CATEGORY_COLORS[category] || CATEGORY_COLORS.AVISO;
  return (
    <span
      className="news-category-badge"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text }}
    >
      {category}
    </span>
  );
}

const onCoverError = (e) => {
  if (e.target.src.includes('/news-fallbacks/')) {
    e.target.style.display = 'none';
  } else {
    e.target.src = '/news-fallbacks/default.jpg';
  }
};

const destOf = (item) => (item.slug ? `/noticias/${item.slug}` : '/noticias');

/* ── Portada destacada ── */
function HeroCard({ item }) {
  return (
    <Link to={destOf(item)} className="news-hero">
      <img className="news-hero-cover" src={getCoverUrl(item)} alt={item.title} onError={onCoverError} />
      <div className="news-hero-body">
        <div className="news-hero-top">
          <CategoryBadge category={item.category} />
          <span className="news-new-tag">Nuevo</span>
          <span className="news-date">{item.published_at}</span>
        </div>
        <h2 className="news-hero-title">{item.title}</h2>
        {item.excerpt && <p className="news-hero-excerpt">{item.excerpt}</p>}
        <span className="news-cta">Leer noticia <ArrowRight size={14} /></span>
      </div>
    </Link>
  );
}

/* ── Tarjeta de la rejilla (imagen arriba) ── */
function FeedCard({ item }) {
  return (
    <Link to={destOf(item)} className="nf-card">
      <div className="nf-cover">
        <span className="nf-badge"><CategoryBadge category={item.category} /></span>
        <img src={getCoverUrl(item)} alt={item.title} onError={onCoverError} />
      </div>
      <div className="nf-body">
        <div className="nf-meta">
          <span className="news-date">{item.published_at}</span>
        </div>
        <h3 className="nf-title">{item.title}</h3>
        <p className="nf-excerpt">{item.excerpt}</p>
        <div className="nf-foot">
          <span className="nf-author">Por {item.author}</span>
          <span className="news-cta">Leer <ArrowRight size={13} /></span>
        </div>
      </div>
    </Link>
  );
}

export default function NoticiasPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/news?limit=20`)
      .then(({ data }) => setNews(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory
    ? news.filter((n) => n.category === activeCategory)
    : news;

  const [hero, ...rest] = filtered;

  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Noticias | Parches y eventos"
        description="Últimas noticias de Frostbound: parches 3.3.5a, eventos, mantenimiento y novedades del servidor WoW en español."
        canonical="/noticias"
      />
      <Navbar />

      <main className="news-main">
        <div className="dl-header anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="shop-title" data-testid="noticias-heading">
            <Newspaper size={30} className="shop-title-icon" />
            NOTICIAS
          </h1>
          <p className="shop-subtitle">
            Todo lo que ocurre en{' '}
            <span className="cyan-highlight">Azeroth.</span>
          </p>
        </div>

        <div className="shop-filters anim-fade-up" style={{ animationDelay: '0.2s' }} data-testid="news-filters">
          {ACTIVE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${activeCategory === cat ? 'filter-btn--active' : ''}`}
              onClick={() => setActiveCategory((prev) => (prev === cat ? null : cat))}
              data-testid={`news-filter-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="armory-loading" data-testid="news-loading">Cargando noticias...</div>
        ) : filtered.length === 0 ? (
          <div className="news-empty" data-testid="news-empty">
            No hay noticias en esta categoría.
          </div>
        ) : (
          <div className="anim-fade-up" style={{ animationDelay: '0.3s', width: '100%' }} data-testid="news-grid">
            {hero && <HeroCard item={hero} />}
            {rest.length > 0 && (
              <div className="news-feed">
                {rest.map((item) => (
                  <FeedCard key={item._id || item.slug} item={item} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <Footer />
    </PageWrapper>
  );
}
